import styled, { css, keyframes } from 'styled-components'
import { stoColor, stoMedia, stoRadius, stoShadow, stoSpace, stoType } from 'constants/stoTheme'

/**
 * The reference tabs (animate-ui, Radix) in CSS only: the motion spring `stiffness 200,
 * damping 25` sampled into a `linear()` easing, and the 0.5s fade-and-blur of new content.
 */
const SPRING = `553ms linear(0, 0.0258, 0.0896, 0.1748, 0.27, 0.3669, 0.4604, 0.5471, 0.6253, 0.6941, 0.7535, 0.804, 0.8462, 0.881, 0.9094, 0.9322, 0.9502, 0.9644, 0.9753, 0.9835, 0.9897, 0.9942, 0.9974, 0.9996, 1.001, 1.0019, 1.0024, 1.0026, 1.0026, 1.0025, 1.0023, 1.0021, 1)`
const CONTENT = '500ms ease-in-out'

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    animation: none;
  }
`

interface HighlightProps {
  $index: number
  $count: number
  /** Gap between tabs and inset from the list edge, px. */
  $gap?: number
  $inset?: number
}

/**
 * The surface behind the active tab, sliding to it. Equal-width tabs let CSS place it from
 * the index alone, so it is exact on the server render too; the list needs `position: relative`.
 */
export const TabHighlightBase = styled.span<HighlightProps>`
  position: absolute;
  top: ${({ $inset = 0 }) => $inset}px;
  bottom: ${({ $inset = 0 }) => $inset}px;
  left: ${({ $inset = 0 }) => $inset}px;
  z-index: 0;
  width: ${({ $count, $gap = 0, $inset = 0 }) => `calc((100% - ${2 * $inset + $gap * ($count - 1)}px) / ${$count})`};
  transform: translateX(${({ $index, $gap = 0 }) => `calc(${$index} * (100% + ${$gap}px))`});
  pointer-events: none;
  /* Browsers without linear() ignore the second line and keep a plain ease-out. */
  transition: transform 553ms cubic-bezier(0.22, 1, 0.36, 1);
  transition: transform ${SPRING};
  ${reducedMotion};
`

/** Tab label colour eases with the highlight. */
export const tabColorTransition = css`
  transition: color ${CONTENT};
  ${reducedMotion};
`

const enter = keyframes`
  from {
    opacity: 0;
    filter: blur(4px);
  }
`

/**
 * New tab content fades in from a blur. Key it by the active tab so it replays on each
 * switch; `$animate` is off until the first switch, so the page does not fade in on load.
 */
export const TabContent = styled.div<{ $animate: boolean }>`
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${enter} ${CONTENT};
    `}
  ${reducedMotion};
`

/**
 * The row's bottom rule shows under every tab but the active one: the sliding white highlight
 * covers it. `$fit` (Dividends): tabs hug their labels, left-aligned, the rest of the row grey.
 */
export const TabList = styled.div<{ $fit?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  background: ${stoColor.surfaceAlt};
  box-shadow: inset 0 -1px 0 ${stoColor.borderSubtle};

  ${({ $fit }) =>
    $fit &&
    css`
      overflow-x: auto;
      /* Clips the highlight's 1px overhang instead of scrolling by it. */
      overflow-y: hidden;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    `}
`

/**
 * The white active-tab surface, sliding between tabs. Runs 1px past the row's bottom rule: an
 * edge drawn exactly on the rule lets a hairline of it through (the moving highlight is its own
 * layer, with anti-aliased edges).
 */
export const TabHighlight = styled(TabHighlightBase)`
  bottom: -1px;
  background: ${stoColor.surface};
`

/** `$fit` tabs differ in width, so the highlight is placed from the measured active tab. */
export const FitHighlight = styled.span`
  position: absolute;
  top: 0;
  /* Past the bottom rule, as TabHighlight. */
  bottom: -1px;
  left: 0;
  z-index: 0;
  width: var(--tab-width);
  transform: translateX(var(--tab-left));
  background: ${stoColor.surface};
  pointer-events: none;
  transition:
    transform 553ms cubic-bezier(0.22, 1, 0.36, 1),
    width 553ms cubic-bezier(0.22, 1, 0.36, 1);
  transition:
    transform ${SPRING},
    width ${SPRING};
  ${reducedMotion};
`

/** `$filled`: the active `$fit` tab paints its own surface until the highlight is measured. */
export const Tab = styled.button<{ $active: boolean; $fit?: boolean; $stretch?: boolean; $filled?: boolean }>`
  ${stoType.tabLabel};
  ${tabColorTransition};
  position: relative;
  z-index: 1;
  /* Equal widths, so the highlight can slide by index: labels are centred, so a narrow
     side padding looks the same as Figma's 32px while not forcing a tab wider. */
  flex: 1 1 0;
  min-width: 0;
  padding: ${stoSpace[4]} ${stoSpace[2]};
  border: none;
  cursor: pointer;
  background: transparent;
  /* Figma: closed tabs in #9b9b9b, a lighter grey than the tertiary text token. */
  color: ${({ $active }) => ($active ? stoColor.textPrimary : stoColor.neutral500)};

  ${({ $fit, $stretch, $filled }) =>
    $fit &&
    css`
      flex: 0 0 auto;
      padding: ${stoSpace[4]} ${stoSpace[8]};
      white-space: nowrap;
      background: ${$filled ? stoColor.surface : 'transparent'};

      /* Figma mobile: the tabs share the row (still scrolling once the labels outgrow it). */
      ${$stretch &&
      css`
        ${stoMedia.belowDesktop} {
          flex: 1 0 auto;
          padding: ${stoSpace[4]} ${stoSpace[5]};
        }
      `}
    `}
`

/**
 * Never shrinks: in a row that overflows (fit tabs on mobile), it would squeeze to 0.
 * `$edge`: the closing divider of stretched tabs, which below desktop would sit on the card's edge.
 */
export const TabDivider = styled.div<{ $edge?: boolean }>`
  position: relative;
  z-index: 1;
  flex: 0 0 1px;
  width: 1px;
  background: ${stoColor.borderSubtle};

  ${({ $edge }) =>
    $edge &&
    css`
      ${stoMedia.belowDesktop} {
        display: none;
      }
    `}
`

/** A card whose header is the tab bar (Figma 3329:24721): Dividends, Performance. */
export const TabCard = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  box-shadow: ${stoShadow.xs};

  /* Tabs are deep-link targets: clear the sticky header. */
  [role='tab'] {
    scroll-margin-top: 96px;

    ${stoMedia.tabletUp} {
      scroll-margin-top: 104px;
    }
  }
`

/** The tab panel's content area: 24px, 16px on mobile (Figma 3582:127370). */
export const TabCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[4]};
  padding: ${stoSpace[6]};

  ${stoMedia.belowDesktop} {
    padding: ${stoSpace[4]};
  }
`
